import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';

import { isPresent } from '../../utils/utils';

const SESSION_ID_STORAGE_KEY = 'finnaircom.X-Session-Id';
const SANITIZE_KEYS = ['hash', 'cartId', 'orderId'];

// Placeholder used until the dapi interceptor has generated the session id on the first request.
const NO_SESSION_ID = 'N/A';

export const getSessionId = (): string =>
  (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_ID_STORAGE_KEY)) || NO_SESSION_ID;

const sanitizeData = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return value.replace(/(hash|cartId|orderId)=(\w+(==)?)/g, '$1=********');
  } else if (Array.isArray(value)) {
    return value.map(sanitizeData);
  } else if (typeof value === 'object' && value !== null) {
    return sanitizeObject(value as Record<string, unknown>);
  }
  return value;
};

export const sanitizeObject = (input: Record<string, unknown>): Record<string, unknown> =>
  Object.keys(input).reduce<Record<string, unknown>>((all, key) => {
    all[key] = SANITIZE_KEYS.includes(key) ? '********' : sanitizeData(input[key]);
    return all;
  }, {});

const getHeaders = (headers: unknown): { headers?: Record<string, string> } => {
  if (!(headers instanceof HttpHeaders)) {
    return {};
  }
  return {
    headers: headers.keys().reduce<Record<string, string>>((acc, key) => {
      const values = headers.getAll(key);
      if (values) {
        acc[key] = values.join(',');
      }
      return acc;
    }, {}),
  };
};

/** Enriches an error/extra into flat attributes, spreading HttpErrorResponse details + headers. */
export const getExtra = (extra: unknown): Record<string, unknown> => {
  if (!extra || typeof extra !== 'object') {
    return {};
  }

  if (extra instanceof HttpErrorResponse) {
    return { ...extra, ...getHeaders(extra.headers || {}) };
  }

  return Object.entries(extra).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value instanceof HttpErrorResponse) {
      return { ...acc, ...value, ...getHeaders(value.headers || {}) };
    }
    return { ...acc, [key]: value };
  }, {});
};

export const normalizeUrl = (
  rawUrl: string
): { domain: string; normalizedPath: string; queryParams: Record<string, string> } => {
  try {
    const parsedUrl = new URL(rawUrl);

    const normalizedPath = parsedUrl.pathname
      .replace(/\b\d+\b/g, ':id')
      .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, ':uuid')
      .replace(/\b(?=[a-zA-Z]*\d)(?=\d*[a-zA-Z])[a-zA-Z0-9]{6,}\b/g, ':code')
      .replace(/\b[A-Z]{6}\b/g, ':code')
      .replace(/\/+/g, '/')
      .replace(/\/+$/, '');

    const queryParams: Record<string, string> = Array.from(parsedUrl.searchParams.entries()).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      {}
    );

    return { domain: parsedUrl.hostname, normalizedPath, queryParams };
  } catch {
    return { domain: '', normalizedPath: rawUrl, queryParams: {} };
  }
};

export const normalizeMessage = (
  input: string | HttpErrorResponse
): { message: string; queryParams: Record<string, string> } => {
  if (!isPresent(input)) {
    return { message: '', queryParams: {} };
  }

  const isHttpError = input instanceof HttpErrorResponse;
  const extractedUrl: string | undefined = isHttpError ? (input.url ?? undefined) : undefined;
  const rawMessage: string = isHttpError ? (input.message ?? 'unknown') : input;
  const url: string | undefined = extractedUrl ?? rawMessage.match(/https?:\/\/[^\s)'"<>]+/)?.[0] ?? undefined;

  if (url) {
    const status: string | undefined = isHttpError ? input.status?.toString() : undefined;
    const statusText: string | undefined = isHttpError ? input.statusText : undefined;
    const eventName: string = isHttpError ? (input.error?.key ?? 'HTTP_ERROR') : 'EVENT_ERROR';

    const { domain, normalizedPath, queryParams } = normalizeUrl(url);

    const processedMessage =
      domain && normalizedPath ? `${eventName} ${status} ${statusText} at ${domain}${normalizedPath}` : rawMessage;

    return { message: processedMessage, queryParams };
  }

  return { message: rawMessage, queryParams: {} };
};

/** Rich, sanitized attributes for a captured error/extra — shared by the Sentry and Datadog backends. */
export const buildErrorContext = (source: unknown): Record<string, unknown> =>
  sanitizeObject({ ...getExtra(source ?? {}), 'session.id': getSessionId() });
